//// Application Packages
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const RegisteredNursingExaminationNonUsGraduatesForm = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                FRGNGRD: string().required(
                    'This field is required to be entered or selected.'
                ),
            })
            schema.valid = {
                FRGNGRD: false,
                FRGNGRDErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Florida Center For Nursing: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Non-U.S. Graduates: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Non-U.S. Graduates: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Non-U.S. Graduates Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        if (event?.key === 'Enter') {
            await handleSubmit()
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        Applicants educated outside the U.S., or graduates from
                        U.S. territories whose regulatory nursing board is not a
                        member of the National Council of State Boards of
                        Nursing (NCSBN) are required to have a{' '}
                        <strong>full education credentials review</strong> by a
                        Florida board-approved credentialing agency.
                    </p>

                    <p>
                        An original copy of the credentials report must be sent
                        electronically to the board directly from the agency.
                        The board does not accept paper copies. Applicants are
                        responsible for paying all fees the agency charges for
                        these services.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FRGNGRD
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FRGNGRD
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FRGNGRD
                        }
                        legendText="Did you graduate from a nursing program outside of the United States, Guam, American Samoa, U.S. Virgin Islands, or Northern Mariana Islands?"
                        name="FRGNGRD"
                        invalid={formsData?.[digital_name]?.errors?.FRGNGRD}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FRGNGRDErrMsg
                        }
                    >
                        <RadioButton
                            id="FRGNGRDYes"
                            name="FRGNGRDYes"
                            labelText="Yes"
                            value="2"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="FRGNGRDNo"
                            name="FRGNGRDNo"
                            labelText="No"
                            value="1"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <p>
                        <strong>
                            Applicants educated outside the U.S., or graduates
                            from U.S. territories
                        </strong>{' '}
                        whose regulatory nursing board is{' '}
                        <strong>not a member of the NCSBN</strong> are required
                        to provide proof of English competency if textbooks and
                        instruction were not completed in English.
                    </p>

                    <p>
                        Board-approved Education Evaluation Providers and
                        English competency exams can be found at{' '}
                        <a
                            href="https://floridasnursing.gov/forms/licensure-info-edu-outside-us.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            https://floridasnursing.gov/forms/licensure-info-edu-outside-us.pdf
                        </a>
                        . Click on the{' '}
                        <strong>{`"Out of State/International"`}</strong> link.
                    </p>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default RegisteredNursingExaminationNonUsGraduatesForm
